import { Input, InputNumber, Segmented, Switch } from "antd";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import ConditionsEditor from "shared/components/ConditionsEditor";
import PayloadPathSelect from "shared/components/PayloadPaths";
import TransformsEditor from "shared/components/TransformsEditor";
import { NAME_SPACES } from "shared/locales/constants";
import {
  PART_MODE_OPTIONS,
  SOURCE_MODES,
  SOURCE_MODE_OPTIONS,
} from "screens/Private/Admin/DynamicDocuments/Create/constants";

const MAX_NESTING = 2;

export const getSourceMode = (mapping = {}) => {
  if (mapping.combine) return SOURCE_MODES.COMBINE;
  if (mapping.from) return SOURCE_MODES.FROM;
  if (mapping.keys) return SOURCE_MODES.KEYS;
  if (mapping.static !== undefined) return SOURCE_MODES.STATIC;
  return SOURCE_MODES.KEY;
};

const withMode = (mapping, mode) => {
  const { key, keys, from, combine, static: literal, ...rest } = mapping;
  if (mode === SOURCE_MODES.KEYS)
    return { ...rest, keys: keys || (key ? [key] : [""]) };
  if (mode === SOURCE_MODES.FROM)
    return { ...rest, from: from || { path: "", where: {}, pick: "" } };
  if (mode === SOURCE_MODES.COMBINE)
    return {
      ...rest,
      combine: combine || {
        separator: " ",
        parts: key ? [{ key }] : [{ key: "" }],
      },
    };
  if (mode === SOURCE_MODES.STATIC) return { ...rest, static: literal ?? "" };
  return { ...rest, key: key || (keys && keys[0]) || "" };
};

const WhereField = ({ value = {}, onChange, placeholder }) => {
  const text = Object.entries(value)
    .map(([key, val]) => `${key}=${val}`)
    .join("\n");

  const parse = (raw) =>
    raw
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .reduce((filter, line) => {
        const idx = line.indexOf("=");
        if (idx <= 0) return filter;
        filter[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
        return filter;
      }, {});

  return (
    <Input.TextArea
      rows={3}
      size="small"
      placeholder={placeholder}
      className="dynamic-documents__field-full dynamic-documents__field-mono"
      value={text}
      onChange={(e) => onChange(parse(e.target.value))}
    />
  );
};

const EMPTY_CONDITIONS = { operator: "AND", rules: [] };

const partConditionProps = (part, onChange) => {
  const conditions = part.conditions || EMPTY_CONDITIONS;
  const commit = (next) =>
    onChange({
      ...part,
      conditions: next.rules.length > 0 ? next : undefined,
    });
  return {
    conditions,
    onOperatorChange: (operator) => commit({ ...conditions, operator }),
    onAddRule: () =>
      commit({
        ...conditions,
        rules: [...conditions.rules, { key: "", op: "equals", value: "" }],
      }),
    onRemoveRule: (idx) =>
      commit({
        ...conditions,
        rules: conditions.rules.filter((_, i) => i !== idx),
      }),
    onUpdateRule: (idx, patch) =>
      commit({
        ...conditions,
        rules: conditions.rules.map((rule, i) =>
          i === idx ? { ...rule, ...patch } : rule,
        ),
      }),
  };
};

const Field = ({ label, hint, children }) => (
  <div className="dynamic-documents__transform-field">
    <div className="dynamic-documents__config-field-label">{label}</div>
    {children}
    {hint && <div className="dynamic-documents__config-field-hint">{hint}</div>}
  </div>
);

const SourceEditor = ({ mapping = {}, onChange, depth = 0 }) => {
  const { t } = useTranslation(NAME_SPACES.COMPONENTS);
  const SOURCE_EDITOR = t("SOURCE_EDITOR", { returnObjects: true });

  const mode = getSourceMode(mapping);
  const set = (patch) => onChange({ ...mapping, ...patch });
  const setFrom = (patch) =>
    onChange({ ...mapping, from: { ...mapping.from, ...patch } });
  const setCombine = (patch) =>
    onChange({ ...mapping, combine: { ...mapping.combine, ...patch } });
  const setPart = (idx, next) =>
    setCombine({
      parts: (mapping.combine?.parts || []).map((p, i) =>
        i === idx ? next : p,
      ),
    });

  const isPart = depth > 0;
  const options = (isPart ? PART_MODE_OPTIONS : SOURCE_MODE_OPTIONS).filter(
    ({ value }) =>
      value !== SOURCE_MODES.COMBINE ||
      depth < MAX_NESTING ||
      mode === SOURCE_MODES.COMBINE,
  );
  const modeKey = options.find((o) => o.value === mode)?.key;

  return (
    <div className="dynamic-documents__source-editor">
      <Segmented
        size="small"
        block
        value={mode}
        onChange={(next) => onChange(withMode(mapping, next))}
        options={options.map(({ value, key }) => ({
          value,
          label: SOURCE_EDITOR.MODES[key].LABEL,
        }))}
      />
      {modeKey && (
        <div className="dynamic-documents__config-field-hint">
          {SOURCE_EDITOR.MODES[modeKey].HINT}
        </div>
      )}

      {mode === SOURCE_MODES.KEY && (
        <Field label={SOURCE_EDITOR.FIELD}>
          <PayloadPathSelect
            value={mapping.key}
            onChange={(key) => set({ key })}
          />
        </Field>
      )}

      {mode === SOURCE_MODES.STATIC && (
        <Field label={SOURCE_EDITOR.STATIC}>
          <Input
            size="small"
            placeholder={SOURCE_EDITOR.STATIC_PLACEHOLDER}
            className="dynamic-documents__field-full"
            value={mapping.static ?? ""}
            onChange={(e) => set({ static: e.target.value })}
          />
        </Field>
      )}

      {mode === SOURCE_MODES.KEYS && (
        <Field
          label={SOURCE_EDITOR.FALLBACKS}
          hint={SOURCE_EDITOR.FALLBACKS_HINT}
        >
          {(mapping.keys || []).map((key, idx) => (
            <div key={idx} className="dynamic-documents__source-row">
              <PayloadPathSelect
                value={key}
                onChange={(next) =>
                  set({
                    keys: (mapping.keys || []).map((k, i) =>
                      i === idx ? next : k,
                    ),
                  })
                }
              />
              <button
                type="button"
                className="conditions-editor__remove-btn"
                onClick={() =>
                  set({
                    keys: (mapping.keys || []).filter((_, i) => i !== idx),
                  })
                }
              >
                <BiTrash size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="conditions-editor__add-btn"
            onClick={() => set({ keys: [...(mapping.keys || []), ""] })}
          >
            <BiPlus size={14} /> {SOURCE_EDITOR.ADD_FALLBACK}
          </button>
        </Field>
      )}

      {mode === SOURCE_MODES.FROM && (
        <>
          <Field label={SOURCE_EDITOR.LIST} hint={SOURCE_EDITOR.LIST_HINT}>
            <PayloadPathSelect
              value={mapping.from?.path}
              onChange={(path) => setFrom({ path })}
            />
          </Field>
          <Field label={SOURCE_EDITOR.WHERE} hint={SOURCE_EDITOR.WHERE_HINT}>
            <WhereField
              value={mapping.from?.where}
              placeholder={SOURCE_EDITOR.WHERE_PLACEHOLDER}
              onChange={(where) => setFrom({ where })}
            />
          </Field>
          <Field label={SOURCE_EDITOR.PICK} hint={SOURCE_EDITOR.PICK_HINT}>
            <Input
              size="small"
              placeholder={SOURCE_EDITOR.PICK_PLACEHOLDER}
              className="dynamic-documents__field-full dynamic-documents__field-mono"
              value={mapping.from?.pick ?? ""}
              onChange={(e) => setFrom({ pick: e.target.value || undefined })}
            />
          </Field>
          <Field label={SOURCE_EDITOR.ALL} hint={SOURCE_EDITOR.ALL_HINT}>
            <Switch
              size="small"
              checked={!!mapping.from?.all}
              onChange={(all) => setFrom({ all: all || undefined })}
            />
          </Field>
          {!mapping.from?.all && (
            <Field label={SOURCE_EDITOR.INDEX} hint={SOURCE_EDITOR.INDEX_HINT}>
              <InputNumber
                size="small"
                className="dynamic-documents__field-full"
                placeholder="0"
                value={mapping.from?.index ?? null}
                onChange={(index) => setFrom({ index: index ?? undefined })}
              />
            </Field>
          )}
          <Field label={SOURCE_EDITOR.DEFAULT}>
            <Input
              size="small"
              className="dynamic-documents__field-full"
              value={mapping.from?.default ?? ""}
              onChange={(e) =>
                setFrom({ default: e.target.value || undefined })
              }
            />
          </Field>
        </>
      )}

      {mode === SOURCE_MODES.COMBINE && (
        <>
          <Field
            label={SOURCE_EDITOR.SEPARATOR}
            hint={SOURCE_EDITOR.SEPARATOR_HINT}
          >
            <Input
              size="small"
              className="dynamic-documents__field-full"
              placeholder={SOURCE_EDITOR.SEPARATOR_PLACEHOLDER}
              value={mapping.combine?.separator ?? ""}
              onChange={(e) => setCombine({ separator: e.target.value })}
            />
          </Field>
          <Field label={SOURCE_EDITOR.PARTS}>
            {(mapping.combine?.parts || []).map((part, idx) => (
              <div key={idx} className="dynamic-documents__part">
                <div className="dynamic-documents__part-header">
                  <span className="dynamic-documents__part-title">
                    {t("SOURCE_EDITOR.PART", { index: idx + 1 })}
                  </span>
                  <button
                    type="button"
                    className="conditions-editor__remove-btn"
                    onClick={() =>
                      setCombine({
                        parts: mapping.combine.parts.filter(
                          (_, i) => i !== idx,
                        ),
                      })
                    }
                  >
                    <BiTrash size={14} />
                  </button>
                </div>
                <SourceEditor
                  mapping={part}
                  depth={depth + 1}
                  onChange={(next) => setPart(idx, next)}
                />
                <TransformsEditor
                  transforms={part.transforms || []}
                  onChange={(transforms) =>
                    setPart(idx, {
                      ...part,
                      transforms:
                        transforms.length > 0 ? transforms : undefined,
                    })
                  }
                />
                <ConditionsEditor
                  {...partConditionProps(part, (next) => setPart(idx, next))}
                  emptyMessage={SOURCE_EDITOR.PART_CONDITIONS_EMPTY}
                />
              </div>
            ))}
            <div className="dynamic-documents__source-row">
              <button
                type="button"
                className="conditions-editor__add-btn"
                onClick={() =>
                  setCombine({
                    parts: [...(mapping.combine?.parts || []), { key: "" }],
                  })
                }
              >
                <BiPlus size={14} /> {SOURCE_EDITOR.ADD_FIELD}
              </button>
              <button
                type="button"
                className="conditions-editor__add-btn"
                onClick={() =>
                  setCombine({
                    parts: [...(mapping.combine?.parts || []), { static: "" }],
                  })
                }
              >
                <BiPlus size={14} /> {SOURCE_EDITOR.ADD_STATIC}
              </button>
            </div>
          </Field>
        </>
      )}
    </div>
  );
};

export default SourceEditor;
